import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, Button, Tag, Space, Modal, Form, Input, InputNumber, Select, 
  message, Typography, Row, Col, Card, Tooltip, Popconfirm 
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, CheckCircleOutlined,
  CloseCircleOutlined, FileTextOutlined, SafetyCertificateOutlined, BankOutlined,
  UserOutlined, CopyOutlined, CalendarOutlined, PhoneOutlined, HistoryOutlined,
  SwapOutlined, RollbackOutlined, PlusCircleOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import StandardFilterPanel from '../../components/hr/StandardFilterPanel';
import PersianDatePicker from '../../components/PersianDatePicker';
import { formatDate, toJalali } from '../../utils/dateUtils';
import { buildCommonDatePresets, parseJalaliDateRangeValue } from '../../utils/filterDateUtils';
import { financialApi } from '../../utils/financialApi';
import ClientSelect from '../../components/common/ClientSelect';
import useRemoteClientOptions from '../../hooks/useRemoteClientOptions';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { apiRequest } from '../../utils/api';
import { getApiUrl } from '../../config/api';
import { 
  BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS, PANEL_DANGER_OK_BUTTON_PROPS 
} from '../../constants/ui';

const { Title, Text } = Typography;
const { Option } = Select;

const toLatinDigits = (value) => {
  if (!value) return value;
  return String(value).replace(/[۰-۹]/g, w => String.fromCharCode(w.charCodeAt(0) - 1728));
};

const amountToPersianWords = (n) => {
  if (!n || isNaN(n)) return '';
  n = Math.floor(Number(n));
  if (n === 0) return 'صفر تومان';
  const units = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
  const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
  const tens = ['', 'ده', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
  const hundreds = ['', 'یکصد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
  const scales = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون'];

  function chunkToWords(num) {
    let parts = [];
    const h = Math.floor(num / 100);
    const rem = num % 100;
    if (h > 0) parts.push(hundreds[h]);
    if (rem >= 10 && rem <= 19) {
      parts.push(teens[rem - 10]);
    } else {
      const t = Math.floor(rem / 10);
      const u = rem % 10;
      if (t > 0) parts.push(tens[t]);
      if (u > 0) parts.push(units[u]);
    }
    return parts.join(' و ');
  }

  let chunks = [];
  let temp = n;
  while (temp > 0) {
    chunks.push(temp % 1000);
    temp = Math.floor(temp / 1000);
  }

  let resParts = [];
  for (let i = chunks.length - 1; i >= 0; i--) {
    const chunk = chunks[i];
    if (chunk > 0) {
      const words = chunkToWords(chunk);
      const scale = scales[i];
      resParts.push(scale ? words + ' ' + scale : words);
    }
  }
  return resParts.join(' و ') + ' تومان';
};

const formatSayadGrouped = (val) => {
  if (!val) return '—';
  const clean = toLatinDigits(val).replace(/\D/g, '');
  if (!clean) return val;
  return clean.replace(/(\d{4})(?=\d)/g, '$1 - ');
};

const getDaysRemainingText = (dueDateStr) => {
  if (!dueDateStr) return null;
  try {
    const due = new Date(dueDateStr);
    const today = new Date();
    if (isNaN(due.getTime())) return null;
    const d1 = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const d2 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffTime = d1 - d2;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return { text: 'سررسید امروز است', color: 'orange' };
    if (diffDays > 0) return { text: `${diffDays} روز مانده به سررسید`, color: 'blue' };
    return { text: `${Math.abs(diffDays)} روز گذشته از سررسید`, color: 'volcano' };
  } catch (e) {
    return null;
  }
};

const STATUS_CONFIG = {
  in_vault: { text: 'نزد صندوق', color: 'warning' },
  deposited: { text: 'در جریان وصول', color: 'processing' },
  cleared: { text: 'پاس‌شده', color: 'success' },
  bounced: { text: 'برگشت‌خورده', color: 'error' },
  returned: { text: 'عودت داده‌شده', color: 'default' },
  spent: { text: 'خرج شده / واگذار شده', color: 'purple' },
};

const DEFAULT_FILTERS = {
  status: 'all',
  client_id: 'all',
  cheque_number: '',
  sayad_number: '',
  drawer_name: '',
  bank_name: '',
  startDate: null,
  endDate: null,
  datePreset: 'all',
};

const ChequeVault = () => {
  const [cheques, setCheques] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [clientDeals, setClientDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [dealInstallments, setDealInstallments] = useState([]);
  const [installmentsLoading, setInstallmentsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [selectedCheque, setSelectedCheque] = useState(null);

  // History & Lifecycle states
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [historyCheque, setHistoryCheque] = useState(null);
  const [historyTimeline, setHistoryTimeline] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyForm] = Form.useForm();
  const [isAddingHistoryNote, setIsAddingHistoryNote] = useState(false);
  const [addingNoteLoading, setAddingNoteLoading] = useState(false);

  // CRUD states
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [viewingCheque, setViewingCheque] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingCheque, setEditingCheque] = useState(null);
  
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [bulkStatusForm] = Form.useForm();
  const [isBulkStatusModalVisible, setIsBulkStatusModalVisible] = useState(false);

  const selectedChequesData = useMemo(() => {
    return cheques.filter((c) => selectedRowKeys.includes(c.id));
  }, [cheques, selectedRowKeys]);

  const totalSelectedAmount = useMemo(() => {
    return selectedChequesData.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  }, [selectedChequesData]);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const handleClientChange = async (clientId, targetForm = form) => {
    targetForm.setFieldsValue({ deal_id: undefined, deal_installment_id: undefined });
    setClientDeals([]);
    setDealInstallments([]);
    if (!clientId) return;

    try {
      setDealsLoading(true);
      const res = await financialApi.getClientDeals(clientId);
      const deals = res?.data?.deals || res?.deals || [];
      setClientDeals(Array.isArray(deals) ? deals : []);
    } catch (err) {
      console.error('Failed to load client deals:', err);
      setClientDeals([]);
    } finally {
      setDealsLoading(false);
    }
  };

  const handleDealChange = async (dealId, targetForm = form) => {
    targetForm.setFieldsValue({ deal_installment_id: undefined });
    setDealInstallments([]);
    if (!dealId) return;

    try {
      setInstallmentsLoading(true);
      const res = await financialApi.getDealInstallments(dealId);
      const installments = res?.data?.installments || res?.installments || [];
      setDealInstallments(Array.isArray(installments) ? installments : []);
    } catch (err) {
      console.error('Failed to load deal installments:', err);
      setDealInstallments([]);
    } finally {
      setInstallmentsLoading(false);
    }
  };

  const buildApiParams = (customFilters = filters, customSearch = searchText, customPagination = pagination) => {
    const params = {
      page: customPagination.current,
      per_page: customPagination.pageSize,
    };
    if (customSearch && customSearch.trim()) {
      params.search = toLatinDigits(customSearch).trim();
    }
    if (customFilters.cheque_number && customFilters.cheque_number.trim()) {
      params.cheque_number = toLatinDigits(customFilters.cheque_number).trim();
    }
    if (customFilters.sayad_number && customFilters.sayad_number.trim()) {
      params.sayad_number = toLatinDigits(customFilters.sayad_number).trim();
    }
    if (customFilters.drawer_name && customFilters.drawer_name.trim()) {
      params.drawer_name = customFilters.drawer_name.trim();
    }
    if (customFilters.bank_name && customFilters.bank_name.trim()) {
      params.bank_name = customFilters.bank_name.trim();
    }
    if (customFilters.status && customFilters.status !== 'all') {
      params.status = customFilters.status;
    }
    if (customFilters.client_id && customFilters.client_id !== 'all') {
      params.client_id = customFilters.client_id;
    }
    if (customFilters.startDate && customFilters.startDate !== 'null' && customFilters.startDate !== 'undefined') {
      params.date_from = customFilters.startDate;
    }
    if (customFilters.endDate && customFilters.endDate !== 'null' && customFilters.endDate !== 'undefined') {
      params.date_to = customFilters.endDate;
    }
    return params;
  };

  const fetchCheques = async (overrideParams = null) => {
    try {
      setLoading(true);
      const params = overrideParams || buildApiParams();
      const res = await financialApi.getCheques(params);
      const chqsData = res?.data?.cheques;
      const list = Array.isArray(chqsData?.data)
        ? chqsData.data
        : (Array.isArray(chqsData) ? chqsData : (Array.isArray(res?.data) ? res.data : []));
      const totalCount = chqsData?.total ?? list.length;

      setCheques(list);
      setPagination((prev) => ({
        ...prev,
        total: totalCount,
      }));
    } catch (error) {
      console.error('Error fetching cheques:', error);
      setCheques([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormData = async () => {
    try {
      const res = await financialApi.getChequeFormData();
      if (res?.data) {
        setBankAccounts(res.data.bank_accounts || []);
      }
    } catch (err) {
      console.error('Failed to load cheque form data', err);
    }
  };

  useEffect(() => {
    fetchFormData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCheques();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, searchText, pagination.current, pagination.pageSize]);

  const handleFilterChange = (key, value) => {
    if (key === 'dateRange') {
      const { startDate, endDate } = parseJalaliDateRangeValue(value);
      setFilters((prev) => ({ 
        ...prev, 
        startDate: startDate || null, 
        endDate: endDate || null,
        datePreset: (startDate || endDate) ? undefined : 'all'
      }));
    } else if (key === 'datePreset') {
      if (!value || value === 'all') {
        setFilters((prev) => ({ ...prev, startDate: null, endDate: null, datePreset: 'all' }));
      } else {
        setFilters((prev) => ({ ...prev, datePreset: value }));
      }
    } else if (key === 'client_id') {
      setFilters((prev) => ({ ...prev, client_id: value || 'all' }));
    } else if (key === 'status') {
      setFilters((prev) => ({ ...prev, status: value || 'all' }));
    } else {
      setFilters((prev) => ({ ...prev, [key]: value || '' }));
    }
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value || '');
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchText('');
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const clientRemote = useRemoteClientOptions({
    selectedValue: filters.client_id === 'all' ? null : filters.client_id,
    includeInactive: true,
  });

  const quickFilters = [
    {
      key: 'status',
      type: 'select',
      label: 'وضعیت',
      placeholder: 'همه وضعیت‌ها',
      value: filters.status === 'all' ? undefined : filters.status,
      options: [
        { value: 'all', label: 'همه وضعیت‌ها' },
        { value: 'in_vault', label: 'نزد صندوق' },
        { value: 'deposited', label: 'در جریان وصول' },
        { value: 'cleared', label: 'پاس‌شده' },
        { value: 'bounced', label: 'برگشت‌خورده' },
        { value: 'returned', label: 'عودت داده‌شده' },
        { value: 'spent', label: 'خرج شده / واگذار شده' },
      ],
    },
    {
      key: 'dateRange',
      type: 'dateRange',
      label: 'بازه تاریخ سررسید',
      placeholder: 'از تاریخ — تا تاریخ (پیش‌فرض: همه تاریخ‌ها)',
      value: filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : undefined,
    },
    {
      key: 'datePreset',
      type: 'datePreset',
      label: 'بازه سریع',
      placeholder: 'بازه زمانی سررسید',
      value: filters.datePreset || (filters.startDate || filters.endDate ? undefined : 'all'),
      options: [
        { value: 'all', label: 'همه تاریخ‌ها (نمایش تمام چک‌ها)', range: { startDate: null, endDate: null } },
        ...buildCommonDatePresets(),
      ],
      onPreset: (range) => {
        setFilters((prev) => ({ 
          ...prev, 
          startDate: range?.startDate || null, 
          endDate: range?.endDate || null,
          datePreset: (range?.startDate || range?.endDate) ? undefined : 'all',
        }));
        setPagination((prev) => ({ ...prev, current: 1 }));
      },
    },
  ];

  const advancedFilters = [
    {
      key: 'cheque_number',
      type: 'input',
      label: 'شماره چک',
      placeholder: 'شماره سریال چک',
      value: filters.cheque_number,
    },
    {
      key: 'sayad_number',
      type: 'input',
      label: 'شماره صیاد',
      placeholder: 'شماره ۱۶ رقمی صیاد',
      value: filters.sayad_number,
    },
    {
      key: 'drawer_name',
      type: 'input',
      label: 'صادرکننده',
      placeholder: 'نام یا کد صادرکننده',
      value: filters.drawer_name,
    },
    {
      key: 'client_id',
      type: 'select',
      label: 'مشتری مرتبط',
      placeholder: 'همه مشتریان',
      value: filters.client_id === 'all' ? undefined : filters.client_id,
      options: clientRemote.options.map((o) => ({
        value: o.value,
        label: o.mobile ? `${o.label} (${toLatinDigits(o.mobile)})` : o.label,
      })),
      ...clientRemote.selectProps,
    },
    {
      key: 'bank_name',
      type: 'input',
      label: 'نام بانک',
      placeholder: 'نام بانک صادرکننده',
      value: filters.bank_name,
    },
  ];

  const handleRegister = async (values) => {
    try {
      // Ensure numeric conversion and proper date formatting
      const dueDate = values.due_date ? (formatDate(values.due_date, 'YYYY-MM-DD', 'gregorian') || String(values.due_date)) : null;
      const payload = {
        ...values,
        client_id: values.client_id ? Number(toLatinDigits(values.client_id)) : null,
        deal_id: values.deal_id ? Number(toLatinDigits(values.deal_id)) : null,
        deal_installment_id: values.deal_installment_id ? Number(toLatinDigits(values.deal_installment_id)) : null,
        amount: Number(toLatinDigits(values.amount)),
        sayad_number: toLatinDigits(values.sayad_number),
        cheque_number: toLatinDigits(values.cheque_number),
        account_number: toLatinDigits(values.account_number),
        drawer_name: values.drawer_name || values.issuer_name,
        issuer_national_id: toLatinDigits(values.issuer_national_id),
        bank_account_id: values.bank_account_id ? Number(toLatinDigits(values.bank_account_id)) : null,
        due_date: dueDate,
      };
      await financialApi.registerCheque(payload);
      message.success('چک با موفقیت ثبت شد');
      setIsModalVisible(false);
      form.resetFields();
      fetchCheques();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ثبت چک');
    }
  };

  const handleChangeStatus = async (values) => {
    if (!selectedCheque) return;
    try {
      const actionDate = values.action_date 
        ? (formatDate(values.action_date, 'YYYY-MM-DD', 'gregorian') || String(values.action_date)) 
        : null;
      const payload = { 
        status: values.status,
        action_date: actionDate,
      };
      if (values.status === 'cleared') {
        payload.bank_account_id = values.bank_account_id ? Number(toLatinDigits(values.bank_account_id)) : null;
        payload.spent_to = values.spent_to ? values.spent_to.trim() : null;
        payload.spent_details = values.spent_details ? values.spent_details.trim() : null;
      }
      if (values.status === 'bounced') {
        payload.bounced_reason = values.bounced_reason ? values.bounced_reason.trim() : null;
      }
      if (values.status === 'spent') {
        payload.spent_to = values.spent_to ? values.spent_to.trim() : null;
        payload.spent_details = values.spent_details ? values.spent_details.trim() : null;
      }
      await financialApi.changeChequeStatus(selectedCheque.id, payload);
      message.success('وضعیت چک تغییر کرد');
      setIsStatusModalVisible(false);
      statusForm.resetFields();
      setSelectedCheque(null);
      fetchCheques();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در تغییر وضعیت');
    }
  };

  const handleOpenHistoryModal = async (cheque) => {
    setHistoryCheque(cheque);
    setIsHistoryModalVisible(true);
    setHistoryLoading(true);
    setIsAddingHistoryNote(false);
    historyForm.resetFields();
    try {
      const res = await financialApi.getChequeHistory(cheque.id);
      if (res?.data?.timeline) {
        setHistoryTimeline(res.data.timeline);
      } else {
        setHistoryTimeline([]);
      }
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در دریافت تاریخچه چک');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAddHistoryNote = async (values) => {
    if (!historyCheque) return;
    setAddingNoteLoading(true);
    try {
      const actionDate = values.action_date 
        ? (formatDate(values.action_date, 'YYYY-MM-DD', 'gregorian') || String(values.action_date)) 
        : null;
      const payload = {
        action: values.action || 'note',
        title: values.title || (values.action === 'spent' ? 'ثبت محل خرج وجه چک' : 'یادداشت و پیگیری'),
        description: values.description || null,
        spent_to: values.spent_to ? values.spent_to.trim() : null,
        spent_details: values.spent_details ? values.spent_details.trim() : null,
        action_date: actionDate,
      };
      const res = await financialApi.addChequeHistory(historyCheque.id, payload);
      message.success(res?.message || 'رویداد تاریخچه با موفقیت ثبت شد');
      if (res?.data?.timeline) {
        setHistoryTimeline(res.data.timeline);
      }
      historyForm.resetFields();
      setIsAddingHistoryNote(false);
      fetchCheques(pagination.current);
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ثبت رویداد تاریخچه');
    } finally {
      setAddingNoteLoading(false);
    }
  };

  const handleEditCheque = async (record) => {
    setEditingCheque(record);
    editForm.resetFields();
    if (record.client_id) {
      try {
        setDealsLoading(true);
        const res = await financialApi.getClientDeals(record.client_id);
        const deals = res?.data?.deals || res?.deals || [];
        setClientDeals(Array.isArray(deals) ? deals : []);
      } catch (err) {
        console.error(err);
      } finally {
        setDealsLoading(false);
      }
    }
    if (record.deal_id) {
      try {
        setInstallmentsLoading(true);
        const res = await financialApi.getDealInstallments(record.deal_id);
        const installments = res?.data?.installments || res?.installments || [];
        setDealInstallments(Array.isArray(installments) ? installments : []);
      } catch (err) {
        console.error(err);
      } finally {
        setInstallmentsLoading(false);
      }
    }

    editForm.setFieldsValue({
      client_id: record.client_id,
      deal_id: record.deal_id,
      deal_installment_id: record.deal_installment_id,
      amount: record.amount,
      sayad_number: record.sayad_number,
      cheque_number: record.cheque_number,
      bank_name: record.bank_name,
      drawer_name: record.drawer_name,
      due_date: record.due_date ? (formatDate(record.due_date, 'YYYY/MM/DD', 'jalali') || record.due_date) : null,
      bank_account_id: record.bank_account_id,
      notes: record.notes,
    });
    setIsEditModalVisible(true);
  };

  const handleUpdateCheque = async (values) => {
    if (!editingCheque) return;
    try {
      const dueDate = values.due_date 
        ? (formatDate(values.due_date, 'YYYY-MM-DD', 'gregorian') || String(values.due_date)) 
        : null;
      const payload = {
        ...values,
        client_id: values.client_id ? Number(toLatinDigits(values.client_id)) : null,
        deal_id: values.deal_id ? Number(toLatinDigits(values.deal_id)) : null,
        deal_installment_id: values.deal_installment_id ? Number(toLatinDigits(values.deal_installment_id)) : null,
        amount: Number(toLatinDigits(values.amount)),
        sayad_number: toLatinDigits(values.sayad_number),
        cheque_number: toLatinDigits(values.cheque_number),
        drawer_name: values.drawer_name,
        bank_name: values.bank_name,
        bank_account_id: values.bank_account_id ? Number(toLatinDigits(values.bank_account_id)) : null,
        due_date: dueDate,
        notes: values.notes,
      };
      await financialApi.updateCheque(editingCheque.id, payload);
      message.success('اطلاعات چک با موفقیت به‌روزرسانی شد');
      setIsEditModalVisible(false);
      setEditingCheque(null);
      editForm.resetFields();
      fetchCheques();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ویرایش چک');
    }
  };

  const handleDeleteCheque = async (id) => {
    try {
      await financialApi.deleteCheque(id);
      message.success('چک با موفقیت حذف شد');
      setSelectedRowKeys((prev) => prev.filter((key) => key !== id));
      fetchCheques();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در حذف چک');
    }
  };

  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('لطفاً حداقل یک چک را انتخاب کنید');
      return;
    }
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف چک‌های انتخاب‌شده</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از حذف {selectedRowKeys.length} چک انتخاب‌شده اطمینان دارید؟ (چک‌های پاس‌شده حذف نخواهند شد)
        </span>
      ),
      okText: 'بله، حذف کن',
      cancelText: 'انصراف',
      okButtonProps: PANEL_DANGER_OK_BUTTON_PROPS,
      onOk: async () => {
        setBulkActionLoading(true);
        try {
          const res = await financialApi.bulkDeleteCheques(selectedRowKeys);
          message.success(res?.message || 'عملیات حذف گروهی با موفقیت انجام شد');
          setSelectedRowKeys([]);
          fetchCheques();
        } catch (error) {
          message.error(extractApiUserMessage(error) || 'خطا در حذف گروهی چک‌ها');
        } finally {
          setBulkActionLoading(false);
        }
      },
    });
  };

  const handleOpenBulkStatusModal = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('لطفاً حداقل یک چک را انتخاب کنید');
      return;
    }
    bulkStatusForm.resetFields();
    setIsBulkStatusModalVisible(true);
  };

  const handleBulkStatusChange = async (values) => {
    if (selectedRowKeys.length === 0) return;
    setBulkActionLoading(true);
    try {
      const payload = {
        ids: selectedRowKeys,
        status: values.status,
      };
      if (values.action_date) {
        payload.action_date = formatDate(values.action_date, 'YYYY-MM-DD', 'gregorian') || String(values.action_date);
      }
      if (values.status === 'cleared') {
        payload.bank_account_id = values.bank_account_id ? Number(toLatinDigits(values.bank_account_id)) : null;
      }
      if (values.status === 'bounced') {
        payload.bounced_reason = values.bounced_reason ? values.bounced_reason.trim() : null;
      }
      if (values.status === 'spent') {
        payload.spent_to = values.spent_to ? values.spent_to.trim() : null;
        payload.spent_details = values.spent_details ? values.spent_details.trim() : null;
      }

      const res = await financialApi.bulkChangeChequeStatus(payload);
      message.success(res?.message || 'عملیات تغییر وضعیت گروهی با موفقیت انجام شد');
      setIsBulkStatusModalVisible(false);
      bulkStatusForm.resetFields();
      setSelectedRowKeys([]);
      fetchCheques();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در تغییر وضعیت گروهی چک‌ها');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleViewCheque = async (record) => {
    try {
      const res = await financialApi.getCheque(record.id);
      const fullCheque = res?.data?.cheque || record;
      setViewingCheque(fullCheque);
    } catch (err) {
      setViewingCheque(record);
    }
    setIsDetailModalVisible(true);
  };

  const handleCopySayad = (sayad) => {
    if (!sayad) return;
    const clean = toLatinDigits(sayad).replace(/\D/g, '');
    if (!clean) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(clean);
      message.success('شناسه صیاد ۱۶ رقمی با موفقیت کپی شد');
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = clean;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      message.success('شناسه صیاد ۱۶ رقمی با موفقیت کپی شد');
    }
  };

  const columns = [
    {
      title: 'شماره چک',
      dataIndex: 'cheque_number',
      key: 'cheque_number',
    },
    {
      title: 'مشتری / صادرکننده',
      key: 'client',
      render: (_, record) => record.client?.name || record.drawer_name || record.issuer_name || '-',
    },
    {
      title: 'مبلغ (تومان)',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatPriceToman(amount),
    },
    {
      title: 'شماره صیاد',
      dataIndex: 'sayad_number',
      key: 'sayad_number',
    },
    {
      title: 'بانک',
      dataIndex: 'bank_name',
      key: 'bank_name',
    },
    {
      title: 'تاریخ سررسید',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date) => {
        if (!date) return '-';
        return <span dir="ltr">{toJalali(date) || '-'}</span>;
      },
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const config = STATUS_CONFIG[status] || { text: status, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'عملیات',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="تاریخچه و گردش چک">
            <Button 
              type="text" 
              size="small"
              icon={<HistoryOutlined className="text-purple-600 dark:text-purple-400" />}
              onClick={() => handleOpenHistoryModal(record)}
            />
          </Tooltip>
          <Tooltip title="مشاهده جزئیات">
            <Button 
              type="text" 
              size="small"
              icon={<EyeOutlined className="text-gray-600 dark:text-gray-300" />}
              onClick={() => handleViewCheque(record)}
            />
          </Tooltip>
          <Tooltip title="ویرایش">
            <Button 
              type="text" 
              size="small"
              icon={<EditOutlined className="text-blue-600 dark:text-blue-400" />}
              onClick={() => handleEditCheque(record)}
            />
          </Tooltip>
          <Tooltip title="تغییر وضعیت">
            <Button 
              type="text" 
              size="small"
              icon={<CheckCircleOutlined className="text-emerald-600 dark:text-emerald-400" />}
              onClick={() => {
                setSelectedCheque(record);
                statusForm.setFieldsValue({ 
                  status: record.status, 
                  bank_account_id: record.bank_account_id,
                  bounced_reason: record.bounced_reason || '' 
                });
                setIsStatusModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="حذف">
            <Popconfirm
              title={<span style={{ fontFamily: 'CustomFont' }}>آیا از حذف این چک اطمینان دارید؟</span>}
              okText="بله، حذف کن"
              cancelText="انصراف"
              okButtonProps={PANEL_DANGER_OK_BUTTON_PROPS}
              onConfirm={() => handleDeleteCheque(record.id)}
            >
              <Button 
                type="text" 
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto animate-fade-in">
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
              خزانه چک صیادی
            </Title>
            <Text className="text-gray-500 dark:text-gray-400">
              مدیریت چک‌های دریافتی، ثبت و تغییر وضعیت آن‌ها
            </Text>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setIsModalVisible(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            ثبت چک جدید
          </Button>
        </div>

        <div className="mb-6">
          <StandardFilterPanel
            searchValue={searchText}
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            searchPlaceholder="جستجو در چک‌ها (شماره چک، صیاد، صادرکننده، نام بانک، مشتری...)"
            quickFilters={quickFilters}
            advancedFilters={advancedFilters}
            onFilterChange={handleFilterChange}
            onResetFilters={handleResetFilters}
            totalCount={pagination.total}
            showAdvancedFilters={true}
            showTotalCount={true}
            compactFilterLayout
          />
        </div>

        {selectedRowKeys.length > 0 && (
          <div className={BULK_ACTION_BAR_CLASS}>
            <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
              {selectedRowKeys.length} مورد انتخاب شده
            </span>
            <Space>
              <Button
                type="primary"
                loading={bulkActionLoading}
                size="middle"
                icon={<SafetyCertificateOutlined />}
                onClick={handleOpenBulkStatusModal}
                style={{ fontFamily: 'CustomFont', backgroundColor: '#10b981', borderColor: '#10b981' }}
              >
                تغییر وضعیت گروهی
              </Button>
              <Button
                type="primary"
                danger
                loading={bulkActionLoading}
                size="middle"
                icon={<DeleteOutlined />}
                onClick={handleBulkDelete}
                style={{ fontFamily: 'CustomFont' }}
              >
                حذف انتخاب‌شده‌ها
              </Button>
            </Space>
          </div>
        )}

        <Table
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          columns={columns}
          dataSource={cheques}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => setPagination(prev => ({ ...prev, current: page, pageSize })),
            showTotal: (total) => `مجموع: ${total} فقره چک`,
          }}
          className="custom-table dark:bg-gray-800 rounded-2xl shadow-sm"
          locale={{ emptyText: 'هیچ چکی در خزانه یافت نشد' }}
        />

        <Modal
          title="ثبت چک صیادی جدید"
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={800}
        >
          <Form layout="vertical" form={form} onFinish={handleRegister}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="client_id" label="مشتری" rules={[{ required: true, message: 'انتخاب مشتری الزامی است' }]}>
                  <ClientSelect
                    onChange={handleClientChange}
                    placeholder="انتخاب یا جستجوی مشتری (نام یا شماره موبایل)"
                    includeInactive
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="deal_id" label="معامله (اختیاری)">
                  <Select
                    showSearch
                    allowClear
                    loading={dealsLoading}
                    placeholder={
                      dealsLoading 
                        ? "در حال دریافت معامله‌ها..." 
                        : (clientDeals.length > 0 ? "انتخاب معامله مشتری" : "معامله‌ای برای این مشتری یافت نشد (اختیاری)")
                    }
                    onChange={handleDealChange}
                    optionFilterProp="children"
                  >
                    {clientDeals.map(d => (
                      <Option key={d.id} value={d.id}>
                        {d.name || `معامله #${d.id}`} {d.value ? `(${formatPriceToman(d.value)})` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="deal_installment_id" label="قسط معامله (اختیاری)">
                  <Select
                    showSearch
                    allowClear
                    loading={installmentsLoading}
                    placeholder={
                      installmentsLoading
                        ? "در حال دریافت اقساط..."
                        : (dealInstallments.length > 0 ? "انتخاب قسط معامله" : "انتخاب قسط (اختیاری)")
                    }
                    optionFilterProp="children"
                  >
                    {dealInstallments.map(inst => (
                      <Option key={inst.id} value={inst.id}>
                        قسط شماره {inst.installment_number || inst.id} - مبلغ: {formatPriceToman(inst.amount)} {inst.due_date ? `(سررسید: ${inst.due_date})` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="amount" label="مبلغ (تومان)" rules={[{ required: true }]}>
                  <InputNumber 
                    className="w-full" 
                    placeholder="مثلا 10000000"
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="sayad_number" label="شماره صیاد" rules={[{ required: true }]}>
                  <Input placeholder="۱۶ رقم صیادی" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="cheque_number" label="شماره چک">
                  <Input placeholder="سریال چک" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="bank_name" label="نام بانک (صادرکننده)" rules={[{ required: true, message: 'نام بانک صادرکننده را وارد کنید' }]}>
                  <Input placeholder="بانک صادرکننده (مثلا ملت، صادرات...)" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="branch_name" label="نام شعبه">
                  <Input placeholder="مثلا شعبه مرکزی" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="account_number" label="شماره حساب">
                  <Input placeholder="شماره حساب صادرکننده" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="drawer_name" label="نام صادرکننده" rules={[{ required: true, message: 'نام صادرکننده چک الزامی است' }]}>
                  <Input placeholder="نام و نام خانوادگی صادرکننده" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="issuer_national_id" label="کد ملی صادرکننده">
                  <Input placeholder="کد ملی ۱۰ رقمی" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="due_date" label="تاریخ سررسید" rules={[{ required: true, message: 'تاریخ سررسید چک الزامی است' }]}>
                  <PersianDatePicker placeholder="انتخاب تاریخ سررسید" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="bank_account_id" label="حساب بانکی شرکت جهت خواباندن/وصول (اختیاری)">
                  <Select
                    allowClear
                    placeholder="انتخاب حساب بانکی شرکت"
                  >
                    {bankAccounts.map(b => (
                      <Option key={b.id} value={b.id}>
                        {b.bank_name || 'بانک'} {b.account_number ? `- ${b.account_number}` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="notes" label="توضیحات">
                  <Input.TextArea placeholder="یادداشت‌های اضافی..." />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item className="mb-0 text-left">
              <Button onClick={() => setIsModalVisible(false)} className="ml-2">انصراف</Button>
              <Button type="primary" htmlType="submit" className="bg-blue-600">ثبت</Button>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={
            <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
              <CheckCircleOutlined className="text-emerald-600" />
              <span>تغییر وضعیت چک</span>
              {selectedCheque && (
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-mono">
                  #{selectedCheque.cheque_number || selectedCheque.id}
                </span>
              )}
            </div>
          }
          open={isStatusModalVisible}
          onCancel={() => {
            setIsStatusModalVisible(false);
            setSelectedCheque(null);
          }}
          footer={null}
          destroyOnClose
        >
          <Form layout="vertical" form={statusForm} onFinish={handleChangeStatus} style={{ fontFamily: 'CustomFont' }}>
            <Form.Item name="status" label="وضعیت جدید" rules={[{ required: true, message: 'انتخاب وضعیت الزامی است' }]}>
              <Select placeholder="انتخاب وضعیت جدید چک">
                <Option value="in_vault">نزد صندوق</Option>
                <Option value="deposited">واگذار به بانک / در جریان وصول</Option>
                <Option value="cleared">پاس‌شده (وصول شده)</Option>
                <Option value="bounced">برگشت‌خورده</Option>
                <Option value="spent">خرج شده / واگذار شده به شخص ثالث</Option>
                <Option value="returned">عودت داده‌شده به مشتری</Option>
              </Select>
            </Form.Item>

            <Form.Item name="action_date" label="تاریخ وقوع رویداد (تاریخ وصول یا برگشت)">
              <PersianDatePicker placeholder="تاریخ رویداد (پیش‌فرض: امروز)" />
            </Form.Item>

            <Form.Item 
              noStyle 
              shouldUpdate={(prev, curr) => prev.status !== curr.status}
            >
              {({ getFieldValue }) => {
                const status = getFieldValue('status');
                if (status === 'cleared') {
                  return (
                    <>
                      <Form.Item name="bank_account_id" label="حساب بانکی برای واریز وجه چک" rules={[{ required: true, message: 'انتخاب حساب بانکی الزامی است' }]}>
                        <Select placeholder="انتخاب حساب بانکی">
                          {bankAccounts.map(b => (
                            <Option key={b.id} value={b.id}>
                              {b.bank_name || 'بانک'} {b.account_number ? `- ${b.account_number}` : ''}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item name="spent_to" label="محل خرج وجه (اختیاری: این مبلغ کجا خرج یا هزینه شد؟)">
                        <Input placeholder="مثال: خرید مواد اولیه، تسویه شرکت پارس، شارژ تنخواه..." />
                      </Form.Item>
                      <Form.Item name="spent_details" label="توضیحات تکمیلی بابت محل خرج (اختیاری)">
                        <Input.TextArea rows={2} placeholder="توضیحات بابت فاکتور یا تامین‌کننده..." />
                      </Form.Item>
                    </>
                  );
                }
                if (status === 'bounced') {
                  return (
                    <Form.Item 
                      name="bounced_reason" 
                      label="علت برگشت چک" 
                      rules={[{ required: true, message: 'وارد کردن علت برگشت چک الزامی است' }]}
                    >
                      <Input.TextArea 
                        rows={3} 
                        placeholder="علت برگشت چک را وارد کنید (مانند: عدم موجودی کافی، نقص یا مغایرت امضا، مسدودی حساب...)" 
                      />
                    </Form.Item>
                  );
                }
                if (status === 'spent') {
                  return (
                    <>
                      <Form.Item 
                        name="spent_to" 
                        label="نام شخص ثالث یا شرکت دریافت‌کننده چک" 
                        rules={[{ required: true, message: 'وارد کردن نام دریافت‌کننده الزامی است' }]}
                      >
                        <Input placeholder="نام تامین‌کننده، شخص یا شرکت دریافت‌کننده چک..." />
                      </Form.Item>
                      <Form.Item name="spent_details" label="بابت چه هزینه یا فاکتوری واگذار شد؟">
                        <Input.TextArea rows={2} placeholder="توضیحات بابت فاکتور خرید، تسویه بدهی و..." />
                      </Form.Item>
                    </>
                  );
                }
                return null;
              }}
            </Form.Item>
            <Form.Item className="mb-0 text-left pt-2">
              <Button onClick={() => setIsStatusModalVisible(false)} className="ml-2">انصراف</Button>
              <Button type="primary" htmlType="submit" className="bg-emerald-600 hover:bg-emerald-700">اعمال تغییر</Button>
            </Form.Item>
          </Form>
        </Modal>

        {/* مودال تغییر وضعیت گروهی چک‌ها */}
        <Modal
          title={
            <div className="flex items-center gap-2.5 py-1" style={{ fontFamily: 'CustomFont' }}>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-lg shadow-sm border border-emerald-100 dark:border-emerald-800">
                <SafetyCertificateOutlined />
              </div>
              <div>
                <span className="text-base font-bold text-gray-800 dark:text-gray-100">
                  تغییر وضعیت گروهی چک‌ها
                </span>
                <span className="text-xs text-gray-400 block font-normal">
                  اعمال وضعیت جدید برای {selectedRowKeys.length} چک انتخاب‌شده
                </span>
              </div>
            </div>
          }
          open={isBulkStatusModalVisible}
          onCancel={() => {
            setIsBulkStatusModalVisible(false);
            bulkStatusForm.resetFields();
          }}
          footer={null}
          destroyOnClose
          width={520}
        >
          <div className="bg-emerald-50 dark:bg-emerald-950/30 p-3.5 rounded-xl border border-emerald-100 dark:border-emerald-900/50 mb-4" style={{ fontFamily: 'CustomFont' }}>
            <div className="flex items-center justify-between mb-1.5 text-sm">
              <span className="text-gray-600 dark:text-gray-300">تعداد چک‌های انتخاب‌شده:</span>
              <span className="font-bold text-gray-800 dark:text-gray-100">{selectedRowKeys.length} فقره چک</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-300">مجموع مبلغ:</span>
              <span className="font-bold text-emerald-700 dark:text-emerald-400 font-mono text-base">
                {formatPriceToman(totalSelectedAmount)}
              </span>
            </div>
            {totalSelectedAmount > 0 && (
              <div className="text-xs text-emerald-800 dark:text-emerald-300 mt-2 pt-2 border-t border-emerald-200/50 dark:border-emerald-800/50">
                <span className="text-gray-400 ml-1">به حروف:</span>
                <span className="font-semibold">{amountToPersianWords(totalSelectedAmount)}</span>
              </div>
            )}
          </div>

          <Form layout="vertical" form={bulkStatusForm} onFinish={handleBulkStatusChange} style={{ fontFamily: 'CustomFont' }}>
            <Form.Item
              name="status"
              label="وضعیت جدید"
              rules={[{ required: true, message: 'انتخاب وضعیت الزامی است' }]}
            >
              <Select placeholder="انتخاب وضعیت جدید برای چک‌های انتخاب‌شده">
                <Option value="in_vault">نزد صندوق</Option>
                <Option value="deposited">واگذار به بانک / در جریان وصول</Option>
                <Option value="cleared">پاس‌شده (وصول شده)</Option>
                <Option value="bounced">برگشت‌خورده</Option>
                <Option value="spent">خرج شده / واگذار شده به شخص ثالث</Option>
                <Option value="returned">عودت داده‌شده به مشتری</Option>
              </Select>
            </Form.Item>

            <Form.Item name="action_date" label="تاریخ وقوع تغییر وضعیت">
              <PersianDatePicker placeholder="تاریخ رویداد (پیش‌فرض: امروز)" />
            </Form.Item>

            <Form.Item 
              noStyle 
              shouldUpdate={(prev, curr) => prev.status !== curr.status}
            >
              {({ getFieldValue }) => {
                const status = getFieldValue('status');
                if (status === 'cleared') {
                  return (
                    <Form.Item
                      name="bank_account_id"
                      label="حساب بانکی مقصد برای واریز وجوه"
                      rules={[{ required: true, message: 'انتخاب حساب بانکی الزامی است' }]}
                      extra="مبلغ تمام چک‌های پاس‌شده به موجودی این حساب بانکی افزوده و اقساط متصل تسویه خواهند شد."
                    >
                      <Select placeholder="انتخاب حساب بانکی جهت واریز...">
                        {bankAccounts.map(b => (
                          <Option key={b.id} value={b.id}>
                            {b.bank_name || 'بانک'} {b.account_number ? `- ${b.account_number}` : ''}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }
                if (status === 'bounced') {
                  return (
                    <Form.Item 
                      name="bounced_reason" 
                      label="علت برگشت چک‌ها" 
                      rules={[{ required: true, message: 'وارد کردن علت برگشت چک‌ها الزامی است' }]}
                    >
                      <Input.TextArea 
                        rows={3} 
                        placeholder="علت برگشت چک‌ها را وارد کنید (مانند: عدم موجودی کافی، نقص یا مغایرت امضا، مسدودی حساب...)" 
                      />
                    </Form.Item>
                  );
                }
                if (status === 'spent') {
                  return (
                    <>
                      <Form.Item 
                        name="spent_to" 
                        label="نام شخص ثالث یا شرکت دریافت‌کننده چک‌ها" 
                        rules={[{ required: true, message: 'وارد کردن نام دریافت‌کننده الزامی است' }]}
                      >
                        <Input placeholder="نام تامین‌کننده، شخص یا شرکت دریافت‌کننده چک‌ها..." />
                      </Form.Item>
                      <Form.Item name="spent_details" label="بابت چه هزینه یا فاکتوری واگذار شدند؟">
                        <Input.TextArea rows={2} placeholder="توضیحات بابت فاکتور خرید، تسویه بدهی و..." />
                      </Form.Item>
                    </>
                  );
                }
                return null;
              }}
            </Form.Item>

            <Form.Item className="mb-0 text-left pt-2">
              <Space>
                <Button onClick={() => setIsBulkStatusModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>
                  انصراف
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={bulkActionLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  اعمال تغییر وضعیت گروهی
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* مودال تاریخچه و گردش چک صیادی (History & Lifecycle Timeline) */}
        <Modal
          title={
            <div className="flex items-center justify-between pl-6 py-1" style={{ fontFamily: 'CustomFont' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center text-lg shadow-sm border border-purple-100 dark:border-purple-800">
                  <HistoryOutlined />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-800 dark:text-gray-100 text-base">تاریخچه و گردش چک صیادی</span>
                    {historyCheque && (
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-mono">
                        #{historyCheque.cheque_number || historyCheque.id}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 block font-normal">
                    رهگیری کامل گردش چک، تاریخ وصول، برگشت، حساب مقصد و محل خرج وجه
                  </span>
                </div>
              </div>
              {historyCheque && (
                <Tag 
                  color={STATUS_CONFIG[historyCheque.status]?.color || 'default'} 
                  className="text-xs px-2.5 py-0.5 rounded-full font-semibold m-0"
                >
                  {STATUS_CONFIG[historyCheque.status]?.text || historyCheque.status}
                </Tag>
              )}
            </div>
          }
          open={isHistoryModalVisible}
          onCancel={() => {
            setIsHistoryModalVisible(false);
            setHistoryCheque(null);
            setHistoryTimeline([]);
            setIsAddingHistoryNote(false);
          }}
          footer={
            <div className="flex items-center justify-between w-full pt-2" style={{ fontFamily: 'CustomFont' }}>
              <Button
                type={isAddingHistoryNote ? 'default' : 'primary'}
                icon={<PlusOutlined />}
                onClick={() => setIsAddingHistoryNote(!isAddingHistoryNote)}
                className={!isAddingHistoryNote ? "bg-purple-600 hover:bg-purple-700" : ""}
              >
                {isAddingHistoryNote ? 'بستن فرم ثبت رویداد' : 'ثبت رویداد جدید / محل خرج'}
              </Button>
              <Button onClick={() => setIsHistoryModalVisible(false)}>
                بستن
              </Button>
            </div>
          }
          width={760}
          destroyOnClose
        >
          {historyCheque && (
            <div style={{ fontFamily: 'CustomFont' }} className="py-1">
              {/* خلاصه مشخصات چک */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 mb-4 text-xs">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <span className="text-gray-400 block mb-0.5">مبلغ چک:</span>
                    <span className="font-bold text-sm text-emerald-600 dark:text-emerald-400 font-mono">
                      {formatPriceToman(historyCheque.amount)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 block mb-0.5">شناسه صیادی:</span>
                    <span className="font-bold font-mono text-gray-700 dark:text-gray-200" dir="ltr">
                      {formatSayadGrouped(historyCheque.sayad_number)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 block mb-0.5">بانک صادرکننده:</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {historyCheque.bank_name ? `بانک ${historyCheque.bank_name}` : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 block mb-0.5">صادرکننده / مشتری:</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {historyCheque.drawer_name || historyCheque.client?.name || '—'}
                    </span>
                  </div>
                </div>
              </div>

              {/* فرم افزودن رویداد / ثبت محل خرج وجه (در صورت باز بودن) */}
              {isAddingHistoryNote && (
                <div className="p-4 rounded-xl bg-purple-50/70 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 mb-5">
                  <div className="font-bold text-sm text-purple-900 dark:text-purple-300 mb-3 flex items-center gap-1.5">
                    <PlusCircleOutlined className="text-purple-600" />
                    <span>ثبت رویداد جدید، پیگیری یا محل خرج وجه</span>
                  </div>
                  <Form
                    form={historyForm}
                    layout="vertical"
                    onFinish={handleAddHistoryNote}
                    initialValues={{ action: 'note', action_date: null }}
                  >
                    <Row gutter={12}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="action"
                          label="نوع رویداد"
                          rules={[{ required: true, message: 'انتخاب نوع رویداد الزامی است' }]}
                        >
                          <Select placeholder="انتخاب نوع رویداد">
                            <Option value="note">یادداشت و پیگیری عمومی</Option>
                            <Option value="spent">ثبت محل خرج / تخصیص وجه چک</Option>
                            <Option value="bank_inquiry">استعلام بانکی / سامانه صیاد</Option>
                            <Option value="customer_contact">تماس و هماهنگی با صادرکننده</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="action_date" label="تاریخ وقوع رویداد">
                          <PersianDatePicker placeholder="تاریخ رویداد (پیش‌فرض: امروز)" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="spent_to" label="کجا خرج شده؟ / نام شخص یا شرکت مقصد">
                          <Input placeholder="مثال: خرید تجهیزات، شرکت تامین‌کننده آریا، تنخواه..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="title" label="عنوان رویداد (اختیاری)">
                          <Input placeholder="مثال: واگذاری بابت تسویه فاکتور ۵۰۴" />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item name="description" label="شرح و جزئیات رویداد">
                          <Input.TextArea rows={2} placeholder="توضیحات تکمیلی..." />
                        </Form.Item>
                      </Col>
                    </Row>
                    <div className="text-left">
                      <Space>
                        <Button onClick={() => setIsAddingHistoryNote(false)}>انصراف</Button>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={addingNoteLoading}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          ثبت در تاریخچه
                        </Button>
                      </Space>
                    </div>
                  </Form>
                </div>
              )}

              {/* تایم‌لاین رویدادها */}
              {historyLoading ? (
                <div className="text-center py-12 text-gray-400">
                  <span>در حال دریافت تاریخچه رویدادهای چک...</span>
                </div>
              ) : historyTimeline.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 dark:bg-slate-800/40 rounded-xl text-gray-500">
                  <ClockCircleOutlined className="text-2xl mb-2 text-gray-400 block" />
                  <span>هنوز رویدادی برای این چک ثبت نشده است.</span>
                </div>
              ) : (
                <div className="relative pr-6 pl-2 my-2 space-y-6 before:content-[''] before:absolute before:top-2 before:bottom-2 before:right-[15px] before:w-0.5 before:bg-gray-200 dark:before:bg-slate-700">
                  {historyTimeline.map((item, idx) => {
                    const isCleared = item.action === 'cleared';
                    const isBounced = item.action === 'bounced';
                    const isSpent = item.action === 'spent';
                    const isDeposited = item.action === 'deposited';
                    const isCreated = item.action === 'created';

                    let nodeColor = 'bg-gray-500 text-white';
                    let borderColor = 'border-gray-200 dark:border-slate-700';
                    let icon = <ClockCircleOutlined />;

                    if (isCleared) {
                      nodeColor = 'bg-emerald-500 text-white ring-4 ring-emerald-100 dark:ring-emerald-950/50';
                      borderColor = 'border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/40 dark:bg-emerald-950/20';
                      icon = <CheckCircleOutlined />;
                    } else if (isBounced) {
                      nodeColor = 'bg-red-500 text-white ring-4 ring-red-100 dark:ring-red-950/50';
                      borderColor = 'border-red-200 dark:border-red-900/60 bg-red-50/40 dark:bg-red-950/20';
                      icon = <CloseCircleOutlined />;
                    } else if (isSpent) {
                      nodeColor = 'bg-purple-500 text-white ring-4 ring-purple-100 dark:ring-purple-950/50';
                      borderColor = 'border-purple-200 dark:border-purple-900/60 bg-purple-50/40 dark:bg-purple-950/20';
                      icon = <SwapOutlined />;
                    } else if (isDeposited) {
                      nodeColor = 'bg-blue-500 text-white ring-4 ring-blue-100 dark:ring-blue-950/50';
                      borderColor = 'border-blue-200 dark:border-blue-900/60 bg-blue-50/40 dark:bg-blue-950/20';
                      icon = <BankOutlined />;
                    } else if (isCreated) {
                      nodeColor = 'bg-amber-500 text-white ring-4 ring-amber-100 dark:ring-amber-950/50';
                      borderColor = 'border-amber-200 dark:border-amber-900/60 bg-amber-50/30 dark:bg-amber-950/20';
                      icon = <PlusCircleOutlined />;
                    }

                    return (
                      <div key={item.id || idx} className="relative flex items-start gap-4">
                        {/* گره تایم‌لاین */}
                        <div className={`absolute -right-[23px] top-1.5 w-7 h-7 rounded-full flex items-center justify-center text-xs shadow-xs z-10 ${nodeColor}`}>
                          {icon}
                        </div>

                        {/* کارت رویداد */}
                        <div className={`w-full p-4 rounded-xl border ${borderColor} transition-all shadow-xs`}>
                          <div className="flex flex-wrap items-center justify-between gap-2 pb-2 mb-2 border-b border-gray-100 dark:border-slate-700/60 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-sm text-gray-800 dark:text-gray-100">
                                {item.title}
                              </span>
                              {item.status_to && (
                                <Tag color={STATUS_CONFIG[item.status_to]?.color || 'default'} className="m-0 text-[11px] rounded">
                                  {STATUS_CONFIG[item.status_to]?.text || item.status_to}
                                </Tag>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 font-mono text-[11px]" dir="ltr">
                              <CalendarOutlined />
                              <span>{item.action_date ? toJalali(item.action_date) : '—'}</span>
                            </div>
                          </div>

                          {item.description && (
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed mb-2 m-0 whitespace-pre-wrap">
                              {item.description}
                            </p>
                          )}

                          {/* اگر حساب بانکی داشت */}
                          {item.bank_account && (
                            <div className="mt-2 p-2 rounded-lg bg-white/80 dark:bg-slate-800/80 border border-gray-200/60 dark:border-slate-700 flex items-center gap-2 text-xs">
                              <BankOutlined className="text-emerald-600" />
                              <span className="text-gray-500">حساب بانکی واریزی:</span>
                              <span className="font-bold text-gray-800 dark:text-gray-200">
                                {item.bank_account.bank_name} {item.bank_account.account_number ? `(${item.bank_account.account_number})` : ''}
                              </span>
                            </div>
                          )}

                          {/* اگر علت برگشت داشت */}
                          {item.metadata?.bounced_reason && (
                            <div className="mt-2 p-2.5 rounded-lg bg-red-100/70 dark:bg-red-950/40 border border-red-200 text-xs text-red-800 dark:text-red-300">
                              <span className="font-bold ml-1">علت برگشت:</span>
                              <span>{item.metadata.bounced_reason}</span>
                            </div>
                          )}

                          {/* اگر محل خرج داشت */}
                          {item.spent_to && (
                            <div className="mt-2 p-2.5 rounded-lg bg-purple-100/70 dark:bg-purple-950/40 border border-purple-200 text-xs text-purple-900 dark:text-purple-300">
                              <span className="font-bold ml-1">محل خرج / واگذاری به:</span>
                              <span className="font-semibold">{item.spent_to}</span>
                              {item.metadata?.spent_details && (
                                <span className="mr-2 text-gray-600 dark:text-gray-400">({item.metadata.spent_details})</span>
                              )}
                            </div>
                          )}

                          {/* فوتر کارت: نام کاربر ثبت‌کننده */}
                          {item.user && (
                            <div className="mt-2 pt-1 text-[11px] text-gray-400 flex items-center gap-1">
                              <UserOutlined />
                              <span>ثبت‌شده توسط {item.user.name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* مودال مشاهده جزئیات چک (Read) */}
        <Modal
          title={
            <div className="flex items-center justify-between pl-6 py-1" style={{ fontFamily: 'CustomFont' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-lg shadow-sm border border-emerald-100 dark:border-emerald-800">
                  <SafetyCertificateOutlined />
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-800 dark:text-gray-100">شناسنامه چک صیادی</span>
                  {viewingCheque && (
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-mono">
                      #{viewingCheque.cheque_number || viewingCheque.id}
                    </span>
                  )}
                </div>
              </div>
              {viewingCheque && (
                <Tag 
                  color={STATUS_CONFIG[viewingCheque.status]?.color || 'default'} 
                  className="text-xs px-2.5 py-0.5 rounded-full font-semibold m-0"
                >
                  {STATUS_CONFIG[viewingCheque.status]?.text || viewingCheque.status}
                </Tag>
              )}
            </div>
          }
          open={isDetailModalVisible}
          onCancel={() => {
            setIsDetailModalVisible(false);
            setViewingCheque(null);
          }}
          width={760}
          footer={
            <div className="flex flex-wrap items-center justify-between gap-2 w-full pt-1" style={{ fontFamily: 'CustomFont' }}>
              <div>
                <Popconfirm
                  title="حذف چک"
                  description="آیا از حذف این فقره چک صیادی اطمینان دارید؟"
                  okText="بله، حذف کن"
                  cancelText="انصراف"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => {
                    const targetId = viewingCheque?.id;
                    setIsDetailModalVisible(false);
                    setViewingCheque(null);
                    if (targetId) handleDeleteCheque(targetId);
                  }}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    حذف چک
                  </Button>
                </Popconfirm>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  icon={<HistoryOutlined className="text-purple-600" />}
                  onClick={() => {
                    const target = viewingCheque;
                    setIsDetailModalVisible(false);
                    if (target) handleOpenHistoryModal(target);
                  }}
                >
                  تاریخچه و گردش چک
                </Button>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => {
                    const target = viewingCheque;
                    setIsDetailModalVisible(false);
                    if (target) handleEditCheque(target);
                  }}
                >
                  ویرایش چک
                </Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    const target = viewingCheque;
                    setIsDetailModalVisible(false);
                    if (target) {
                      setSelectedCheque(target);
                      statusForm.setFieldsValue({ 
                        status: target.status, 
                        bank_account_id: target.bank_account_id,
                        bounced_reason: target.bounced_reason || '' 
                      });
                      setIsStatusModalVisible(true);
                    }
                  }}
                >
                  تغییر وضعیت
                </Button>
                <Button onClick={() => setIsDetailModalVisible(false)}>
                  بستن
                </Button>
              </div>
            </div>
          }
        >
          {viewingCheque && (() => {
            const daysRemaining = getDaysRemainingText(viewingCheque.due_date);
            return (
              <div style={{ fontFamily: 'CustomFont' }} className="py-2">
                {/* 1. کارت دیجیتال چک صیادی (Digital Cheque Card) */}
                <div className="relative overflow-hidden rounded-2xl border border-emerald-200/90 dark:border-emerald-800/40 bg-gradient-to-br from-emerald-50/70 via-teal-50/30 to-slate-50 dark:from-slate-800 dark:via-slate-800/80 dark:to-emerald-950/20 p-5 sm:p-6 shadow-sm mb-5">
                  {/* واترمارک پس‌زمینه */}
                  <div className="absolute -left-6 -bottom-6 text-emerald-900/[0.04] dark:text-emerald-100/[0.03] text-9xl pointer-events-none select-none">
                    <SafetyCertificateOutlined />
                  </div>

                  {/* ردیف هدر کارت: لوگو و نام بانک، شماره سریال و وضعیت */}
                  <div className="relative z-10 flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-emerald-200/50 dark:border-slate-700/80">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-white dark:bg-slate-700 border border-emerald-100 dark:border-slate-600 shadow-sm flex items-center justify-center text-emerald-600 dark:text-emerald-400 text-xl flex-shrink-0">
                        <BankOutlined />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">بانک صادرکننده</div>
                        <div className="text-base font-bold text-gray-800 dark:text-gray-100">
                          {viewingCheque.bank_name ? `بانک ${viewingCheque.bank_name}` : 'بانک صادرکننده نامشخص'}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-left bg-white/70 dark:bg-slate-800/70 px-3 py-1.5 rounded-lg border border-slate-200/60 dark:border-slate-700/60">
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">سریال چک</div>
                        <div className="text-sm font-bold text-gray-800 dark:text-gray-200 font-mono tracking-wider">
                          {viewingCheque.cheque_number || '—'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* شناسه ۱۶ رقمی صیادی */}
                  <div className="relative z-10 mt-4 p-3.5 rounded-xl bg-white/95 dark:bg-slate-900/80 border border-emerald-200/60 dark:border-slate-700 shadow-xs flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-col">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mb-1">
                        شناسه ۱۶ رقمی صیاد (جهت ثبت و استعلام در سامانه صیاد بانک مرکزی)
                      </span>
                      <span className="font-mono text-base sm:text-xl font-bold tracking-widest text-slate-800 dark:text-slate-100 select-all" dir="ltr">
                        {formatSayadGrouped(viewingCheque.sayad_number)}
                      </span>
                    </div>
                    <Tooltip title="کپی شناسه ۱۶ رقمی صیاد">
                      <Button
                        type="dashed"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopySayad(viewingCheque.sayad_number)}
                        className="text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 border-emerald-300 dark:border-emerald-700 bg-emerald-50/60 dark:bg-emerald-950/40 font-medium text-xs rounded-lg px-3 h-8 flex items-center gap-1.5"
                      >
                        کپی شناسه
                      </Button>
                    </Tooltip>
                  </div>

                  {/* بخش مبلغ چک به عدد و به حروف */}
                  <div className="relative z-10 mt-4 bg-white/85 dark:bg-slate-900/60 p-4 rounded-xl border border-emerald-100 dark:border-slate-700/60">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">مبلغ چک:</div>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 font-mono">
                        {formatPriceToman(viewingCheque.amount)}
                      </span>
                    </div>
                    {amountToPersianWords(viewingCheque.amount) && (
                      <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-slate-800/80 text-xs text-gray-600 dark:text-gray-300">
                        <span className="text-gray-500 dark:text-gray-400 ml-1.5 font-medium">مبلغ به حروف:</span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">{amountToPersianWords(viewingCheque.amount)}</span>
                      </div>
                    )}
                  </div>

                  {/* ردیف پایین کارت: تاریخ سررسید و صادرکننده */}
                  <div className="relative z-10 mt-4 pt-3 border-t border-emerald-200/50 dark:border-slate-700/80 flex flex-wrap items-center justify-between gap-4 text-xs">
                    <div className="flex items-center gap-2">
                      <CalendarOutlined className="text-emerald-600 text-base" />
                      <span className="text-gray-500 dark:text-gray-400">تاریخ سررسید:</span>
                      <span className="font-bold text-gray-800 dark:text-gray-200 font-mono text-sm" dir="ltr">
                        {toJalali(viewingCheque.due_date) || '—'}
                      </span>
                      {daysRemaining && (
                        <Tag color={daysRemaining.color} className="text-[11px] px-2 py-0.5 rounded mr-1">
                          {daysRemaining.text}
                        </Tag>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <UserOutlined className="text-emerald-600 text-base" />
                      <span className="text-gray-500 dark:text-gray-400">صادرکننده چک:</span>
                      <span className="font-bold text-gray-800 dark:text-gray-200 text-sm">
                        {viewingCheque.drawer_name || '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 2. اطلاعات ساختاریافته پرونده و بانک در ۲ کارت مجزا */}
                <Row gutter={[16, 16]}>
                  {/* کارت راست: طرفین و پرونده تجاری */}
                  <Col xs={24} md={12}>
                    <div className="p-4 bg-gray-50/90 dark:bg-slate-800/60 border border-gray-200/80 dark:border-slate-700/80 rounded-xl h-full shadow-xs">
                      <div className="text-xs font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 pb-3 mb-3 border-b border-gray-200/60 dark:border-slate-700/60">
                        <UserOutlined className="text-blue-500" />
                        <span>طرفین و پرونده تجاری</span>
                      </div>

                      <div className="space-y-3 text-xs">
                        <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-slate-700/40">
                          <span className="text-gray-500 dark:text-gray-400">مشتری (طرف حساب):</span>
                          <div className="flex items-center gap-1.5 font-bold text-gray-800 dark:text-gray-100">
                            <span>{viewingCheque.client?.name || '—'}</span>
                            {viewingCheque.client?.mobile && (
                              <Tag className="text-[11px] font-mono px-1.5 py-0 rounded bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-0 m-0" dir="ltr">
                                <PhoneOutlined className="ml-1" />
                                {viewingCheque.client.mobile}
                              </Tag>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-slate-700/40">
                          <span className="text-gray-500 dark:text-gray-400">نام صادرکننده:</span>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">
                            {viewingCheque.drawer_name || '—'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-slate-700/40">
                          <span className="text-gray-500 dark:text-gray-400">معامله مرتبط:</span>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">
                            {viewingCheque.deal ? (
                              <span className="text-blue-600 dark:text-blue-400 font-bold">
                                {viewingCheque.deal.name || `معامله #${viewingCheque.deal.id}`}
                              </span>
                            ) : 'فاقد معامله مرتبط'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between py-1">
                          <span className="text-gray-500 dark:text-gray-400">قسط متصل:</span>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">
                            {viewingCheque.installment ? (
                              <Tag color="cyan" className="text-xs m-0">
                                قسط شماره {viewingCheque.installment.installment_number}
                              </Tag>
                            ) : 'فاقد قسط اختصاصی'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* کارت چپ: مشخصات بانکی و تسویه */}
                  <Col xs={24} md={12}>
                    <div className="p-4 bg-gray-50/90 dark:bg-slate-800/60 border border-gray-200/80 dark:border-slate-700/80 rounded-xl h-full shadow-xs">
                      <div className="text-xs font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 pb-3 mb-3 border-b border-gray-200/60 dark:border-slate-700/60">
                        <BankOutlined className="text-emerald-500" />
                        <span>مشخصات بانکی و ثبتی</span>
                      </div>

                      <div className="space-y-3 text-xs">
                        <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-slate-700/40">
                          <span className="text-gray-500 dark:text-gray-400">بانک صادرکننده:</span>
                          <span className="font-bold text-gray-800 dark:text-gray-200">
                            {viewingCheque.bank_name ? `بانک ${viewingCheque.bank_name}` : '—'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-slate-700/40">
                          <span className="text-gray-500 dark:text-gray-400">حساب واریز شرکت:</span>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">
                            {viewingCheque.bank_account ? (
                              <span>
                                {viewingCheque.bank_account.bank_name} {viewingCheque.bank_account.account_number ? `(${viewingCheque.bank_account.account_number})` : ''}
                              </span>
                            ) : 'هنوز تخصیص نیافته'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-slate-700/40">
                          <span className="text-gray-500 dark:text-gray-400">ثبت‌شده توسط:</span>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">
                            {viewingCheque.recorded_by?.name || (typeof viewingCheque.recorded_by === 'string' ? viewingCheque.recorded_by : 'سیستم')}
                          </span>
                        </div>

                        <div className="flex items-center justify-between py-1">
                          <span className="text-gray-500 dark:text-gray-400">تاریخ ثبت در سامانه:</span>
                          <span className="font-semibold text-gray-600 dark:text-gray-400 font-mono" dir="ltr">
                            {viewingCheque.created_at ? toJalali(viewingCheque.created_at) : '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* بنر وضعیت برگشت چک */}
                {viewingCheque.status === 'bounced' && (
                  <div className="mt-4 p-3.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-xl flex items-start gap-3">
                    <CloseCircleOutlined className="text-red-500 text-lg mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="font-bold text-xs sm:text-sm text-red-800 dark:text-red-300">چک برگشت خورده است</div>
                        {viewingCheque.bounced_at && (
                          <span className="text-[11px] font-mono text-red-600 dark:text-red-400" dir="ltr">
                            تاریخ برگشت: {toJalali(viewingCheque.bounced_at)}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-red-700 dark:text-red-400 mt-1">
                        <span className="font-semibold">دلیل برگشت: </span>
                        {viewingCheque.bounced_reason || 'دلیلی در سیستم ثبت نشده است'}
                      </div>
                    </div>
                  </div>
                )}

                {/* بنر وضعیت پاس‌شده */}
                {viewingCheque.status === 'cleared' && (
                  <div className="mt-4 p-3.5 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircleOutlined className="text-emerald-500 text-lg flex-shrink-0" />
                        <span className="text-xs text-emerald-800 dark:text-emerald-300 font-bold">
                          این فقره چک پاس شده و به حساب بانکی شرکت واریز گردیده است.
                        </span>
                      </div>
                      {viewingCheque.cleared_at && (
                        <span className="text-[11px] font-mono text-emerald-700 dark:text-emerald-300" dir="ltr">
                          تاریخ وصول: {toJalali(viewingCheque.cleared_at)}
                        </span>
                      )}
                    </div>
                    {viewingCheque.spent_to && (
                      <div className="mt-2 pt-2 border-t border-emerald-200/60 dark:border-emerald-900/60 text-xs text-emerald-900 dark:text-emerald-200 flex items-center gap-1.5">
                        <span className="font-semibold">محل خرج / تخصیص وجه:</span>
                        <span>{viewingCheque.spent_to}</span>
                        {viewingCheque.spent_details && (
                          <span className="text-emerald-700 dark:text-emerald-400">({viewingCheque.spent_details})</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* بنر وضعیت خرج شده / واگذار شده */}
                {viewingCheque.status === 'spent' && (
                  <div className="mt-4 p-3.5 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900/50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <SwapOutlined className="text-purple-600 text-lg flex-shrink-0" />
                        <span className="text-xs text-purple-900 dark:text-purple-300 font-bold">
                          این فقره چک به شخص ثالث یا شرکت تامین‌کننده واگذار و خرج شده است.
                        </span>
                      </div>
                      {viewingCheque.spent_at && (
                        <span className="text-[11px] font-mono text-purple-700 dark:text-purple-300" dir="ltr">
                          تاریخ واگذاری: {toJalali(viewingCheque.spent_at)}
                        </span>
                      )}
                    </div>
                    {viewingCheque.spent_to && (
                      <div className="mt-2 pt-2 border-t border-purple-200/60 dark:border-purple-900/60 text-xs text-purple-900 dark:text-purple-200 flex items-center gap-1.5">
                        <span className="font-semibold">دریافت‌کننده چک:</span>
                        <span>{viewingCheque.spent_to}</span>
                        {viewingCheque.spent_details && (
                          <span className="text-purple-700 dark:text-purple-400">({viewingCheque.spent_details})</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* یادداشت‌ها و توضیحات */}
                {viewingCheque.notes && (
                  <div className="mt-4 p-3.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/70 rounded-xl">
                    <div className="text-xs font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1.5 mb-1.5">
                      <FileTextOutlined className="text-slate-500" /> یادداشت‌ها و توضیحات
                    </div>
                    <p className="text-xs text-slate-700 dark:text-slate-300 m-0 whitespace-pre-wrap leading-relaxed">
                      {viewingCheque.notes}
                    </p>
                  </div>
                )}
              </div>
            );
          })()}
        </Modal>

        {/* مودال ویرایش چک (Update) */}
        <Modal
          title={
            <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
              <EditOutlined className="text-blue-600" />
              <span>ویرایش چک صیادی #{editingCheque?.cheque_number || editingCheque?.id}</span>
            </div>
          }
          open={isEditModalVisible}
          onCancel={() => {
            setIsEditModalVisible(false);
            setEditingCheque(null);
            editForm.resetFields();
          }}
          footer={null}
          width={800}
        >
          <Form layout="vertical" form={editForm} onFinish={handleUpdateCheque}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="client_id" label="مشتری">
                  <ClientSelect
                    onChange={(val) => handleClientChange(val, editForm)}
                    placeholder="انتخاب یا جستجوی مشتری (نام یا شماره موبایل)"
                    includeInactive
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="deal_id" label="معامله (اختیاری)">
                  <Select
                    showSearch
                    allowClear
                    loading={dealsLoading}
                    placeholder="انتخاب معامله مشتری"
                    onChange={(val) => handleDealChange(val, editForm)}
                    optionFilterProp="children"
                  >
                    {clientDeals.map(d => (
                      <Option key={d.id} value={d.id}>
                        {d.name || `معامله #${d.id}`} {d.value ? `(${formatPriceToman(d.value)})` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="deal_installment_id" label="قسط معامله (اختیاری)">
                  <Select
                    showSearch
                    allowClear
                    loading={installmentsLoading}
                    placeholder="انتخاب قسط معامله"
                    optionFilterProp="children"
                  >
                    {dealInstallments.map(inst => (
                      <Option key={inst.id} value={inst.id}>
                        قسط شماره {inst.installment_number || inst.id} - مبلغ: {formatPriceToman(inst.amount)} {inst.due_date ? `(سررسید: ${inst.due_date})` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="amount" label="مبلغ (تومان)" rules={[{ required: true, message: 'مبلغ چک الزامی است' }]}>
                  <InputNumber 
                    className="w-full" 
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="sayad_number" label="شماره صیاد" rules={[{ required: true, message: 'شماره ۱۶ رقمی صیاد الزامی است' }]}>
                  <Input placeholder="۱۶ رقم صیادی" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="cheque_number" label="شماره چک" rules={[{ required: true, message: 'شماره چک الزامی است' }]}>
                  <Input placeholder="سریال چک" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="bank_name" label="نام بانک (صادرکننده)" rules={[{ required: true, message: 'نام بانک الزامی است' }]}>
                  <Input placeholder="بانک صادرکننده" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="drawer_name" label="نام صادرکننده" rules={[{ required: true, message: 'نام صادرکننده چک الزامی است' }]}>
                  <Input placeholder="نام و نام خانوادگی صادرکننده" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="due_date" label="تاریخ سررسید" rules={[{ required: true, message: 'تاریخ سررسید چک الزامی است' }]}>
                  <PersianDatePicker placeholder="انتخاب تاریخ سررسید" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="bank_account_id" label="حساب بانکی شرکت جهت خواباندن/وصول (اختیاری)">
                  <Select allowClear placeholder="انتخاب حساب بانکی شرکت">
                    {bankAccounts.map(b => (
                      <Option key={b.id} value={b.id}>
                        {b.bank_name || 'بانک'} {b.account_number ? `- ${b.account_number}` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="notes" label="توضیحات">
                  <Input.TextArea placeholder="یادداشت‌های اضافی..." />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item className="mb-0 text-left">
              <Button onClick={() => { setIsEditModalVisible(false); setEditingCheque(null); }} className="ml-2">انصراف</Button>
              <Button type="primary" htmlType="submit" className="bg-blue-600">ذخیره تغییرات</Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default ChequeVault;
